"use strict";
/**
 * Core game rules for backgammon.
 * This file contains move validation and game rules functions.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateTargetIndex = calculateTargetIndex;
exports.isMoveValid = isMoveValid;
exports.canBearOff = canBearOff;
exports.findFurthestCheckerIndex = findFurthestCheckerIndex;
exports.calculateBearOffDistance = calculateBearOffDistance;
exports.isValidBearOff = isValidBearOff;
exports.checkWinCondition = checkWinCondition;
exports.isValidMoveSequence = isValidMoveSequence;
exports.simulateMoveSequence = simulateMoveSequence;
exports.findValidMovesSequence = findValidMovesSequence;
exports.findValidMovesForDie = findValidMovesForDie;
exports.findBestMove = findBestMove;
var tslib_1 = require("tslib");
var constants_1 = require("@/constants");
var state_1 = require("@/game/state");
var utils_1 = require("@/utils");
/**
 * Calculates the target index for a move given a starting point, die value, and player.
 * Handles the different movement directions for white and black players.
 *
 * @param fromIndex - Starting index (-1 for white bar, 24 for black bar)
 * @param die - The die value to use for the move
 * @param player - The player making the move ('white' or 'black')
 * @returns The calculated target index (-1 for white bearing off, 24 for black bearing off)
 */
function calculateTargetIndex(fromIndex, die, player) {
    if (player === constants_1.PLAYER_WHITE) {
        // White moves from higher to lower indices (24→1)
        // From bar (-1) means entering from point 24 (index 23) minus die
        if (fromIndex === constants_1.BAR_INDEX_WHITE) {
            return 24 - die;
        }
        // Standard movement - subtract die from current position
        var targetIndex = fromIndex - die;
        // If result is negative, bearing off
        if (targetIndex < 0) {
            return constants_1.OFF_INDEX_WHITE;
        }
        return targetIndex;
    }
    else {
        // Black moves from lower to higher indices (1→24)
        // From bar (24) means entering from point 1 (index 0) plus die
        if (fromIndex === constants_1.BAR_INDEX_BLACK) {
            return die - 1;
        }
        // Standard movement - add die to current position
        var targetIndex = fromIndex + die;
        // If result is beyond the board, bearing off
        if (targetIndex > 23) {
            return constants_1.OFF_INDEX_BLACK;
        }
        return targetIndex;
    }
}
/**
 * Validates if a move is legal according to backgammon rules.
 * This combines the client's isTargetValid and server's isMoveValidOnBoard functions.
 *
 * @param board - The current board state
 * @param bar - The current bar state (checkers on the bar)
 * @param player - The player making the move
 * @param fromIndex - The starting point index (-1 for white bar, 24 for black bar)
 * @param toIndex - The target point index (-1 for white bearing off, 24 for black bearing off)
 * @param dieValue - The die value being used for this move
 * @returns True if the move is valid, false otherwise
 */
function isMoveValid(gameState, move) {
    var board = gameState.board, bar = gameState.bar;
    var player = gameState.currentPlayerColor;
    var opponent = player === constants_1.PLAYER_WHITE ? constants_1.PLAYER_BLACK : constants_1.PLAYER_WHITE;
    var playerBarIndex = player === constants_1.PLAYER_WHITE ? constants_1.BAR_INDEX_WHITE : constants_1.BAR_INDEX_BLACK;
    var playerOffIndex = player === constants_1.PLAYER_WHITE ? constants_1.OFF_INDEX_WHITE : constants_1.OFF_INDEX_BLACK;
    // 1. If player has checkers on the bar, they must move those first
    if ((player === constants_1.PLAYER_WHITE && bar.white > 0) || (player === constants_1.PLAYER_BLACK && bar.black > 0)) {
        // Only allow moves from the bar
        if (move.from !== playerBarIndex) {
            return false;
        }
    }
    // 2. Check if the player has a checker at the source
    if (move.from === playerBarIndex) {
        // Check if the player has checkers on the bar
        if ((player === constants_1.PLAYER_WHITE && bar.white === 0) ||
            (player === constants_1.PLAYER_BLACK && bar.black === 0)) {
            return false;
        }
    }
    else if (move.from >= 0 && move.from < 24) {
        // Check if the player has checkers at the board position
        if (!board[move.from] || board[move.from].player !== player || board[move.from].count === 0) {
            return false;
        }
    }
    else {
        // Invalid source index (not bar, not on board)
        return false;
    }
    // 3. Verify the die value matches the move
    // For normal board moves, we can use calculateTargetIndex
    var calculatedTargetIndex = calculateTargetIndex(move.from, move.dieValue, player);
    // For debugging, uncomment the following line
    // console.log(`Calculated target: ${calculatedTargetIndex}, Requested target: ${toIndex}, Die: ${move.dieValue}, Player: ${player}, From: ${move.from}`);
    if (calculatedTargetIndex !== move.to) {
        return false;
    }
    // 4. Check if the target position is valid (if it's on the board)
    if (move.to >= 0 && move.to < 24) {
        var targetPoint = board[move.to];
        if (!targetPoint)
            return false;
        // Can move to an empty point
        if (targetPoint.count === 0)
            return true;
        // Can move to a point owned by the player
        if (targetPoint.player === player)
            return true;
        // Can hit a single opponent checker (blot)
        if (targetPoint.player === opponent && targetPoint.count === 1)
            return true;
        // Cannot move to a point blocked by opponent
        return false;
    }
    // 5. Bearing off - will be validated more thoroughly by canBearOff/isValidBearOff
    else if (move.to === playerOffIndex) {
        return isValidBearOff(gameState, player, move.from, move.dieValue);
    }
    // 6. Invalid target index (not on board, not bearing off)
    return false;
}
/**
 * Checks if a player is allowed to bear off checkers.
 * A player can bear off when all their checkers are in their home board and none are on the bar.
 *
 * @param gameState - The current game state
 * @param player - The player to check
 * @returns True if the player can bear off, false otherwise
 */
function canBearOff(state, player) {
    // Player can't bear off if they have checkers on the bar
    if ((player === constants_1.PLAYER_WHITE && state.bar.white > 0) ||
        (player === constants_1.PLAYER_BLACK && state.bar.black > 0)) {
        return false;
    }
    // Determine home board start and end indices
    var homeStart = player === constants_1.PLAYER_WHITE ? constants_1.HOME_BOARD_WHITE_START : constants_1.HOME_BOARD_BLACK_START;
    var homeEnd = player === constants_1.PLAYER_WHITE ? constants_1.HOME_BOARD_WHITE_END : constants_1.HOME_BOARD_BLACK_END;
    // Check if any checkers are outside the home board
    for (var i = 0; i < 24; i++) {
        var point = state.board[i];
        if (point.player === player && point.count > 0) {
            // Check if this checker is outside the home board
            var isOutsideHome = player === constants_1.PLAYER_WHITE
                ? i > homeEnd // For white, outside home if index > 5
                : i < homeStart; // For black, outside home if index < 18
            if (isOutsideHome) {
                return false;
            }
        }
    }
    // All checkers are either in the home board or borne off
    // ADDED CHECK: Ensure opponent does not occupy any points in the player's home board.
    // This is a standard rule: you cannot start bearing off if the opponent has established a point
    // in your home board, as not all your checkers are "home and free".
    /*
    const opponent = player === PLAYER_WHITE ? PLAYER_BLACK : PLAYER_WHITE;
    for (let i = homeStart; i <= homeEnd; i++) { // Iterate through the player's home board
      const point = state.board[i];
      if (point.player === opponent && point.count > 0) {
        return false; // Opponent has a checker in the home board
      }
    }
    */
    return true;
}
/**
 * Finds the index of the checker furthest from being borne off for the given player.
 * For white, this is the highest index within the home board (0-5).
 * For black, this is the lowest index within the home board (18-23).
 *
 * @param board - The current board state
 * @param player - The player
 * @returns The index of the furthest checker, or -1 if none in the home board
 */
function findFurthestCheckerIndex(board, player) {
    var _a, _b;
    // Determine home board range
    var homeStart = player === constants_1.PLAYER_WHITE ? constants_1.HOME_BOARD_WHITE_START : constants_1.HOME_BOARD_BLACK_START;
    var homeEnd = player === constants_1.PLAYER_WHITE ? constants_1.HOME_BOARD_WHITE_END : constants_1.HOME_BOARD_BLACK_END;
    if (player === constants_1.PLAYER_WHITE) {
        // For white, iterate from highest index (5) down to lowest (0)
        for (var i = homeEnd; i >= homeStart; i--) {
            if (((_a = board[i]) === null || _a === void 0 ? void 0 : _a.player) === player && board[i].count > 0) {
                return i;
            }
        }
    }
    else {
        // For black, iterate from lowest index (18) up to highest (23)
        for (var i = homeStart; i <= homeEnd; i++) {
            if (((_b = board[i]) === null || _b === void 0 ? void 0 : _b.player) === player && board[i].count > 0) {
                return i;
            }
        }
    }
    // No checkers found in home board
    return -1;
}
/**
 * Calculates the exact die value needed to bear off from a specific point.
 *
 * @param fromIndex - The point index (0-23)
 * @param player - The player bearing off
 * @returns The exact die value required to bear off from this point
 */
function calculateBearOffDistance(fromIndex, player) {
    if (player === constants_1.PLAYER_WHITE) {
        // For white:
        // Point 1 (index 0) needs die 1 to bear off
        // Point 2 (index 1) needs die 2 to bear off
        // ...and so on
        return fromIndex + 1;
    }
    else {
        // For black:
        // Point 24 (index 23) needs die 1 to bear off
        // Point 23 (index 22) needs die 2 to bear off
        // ...and so on
        return 24 - fromIndex;
    }
}
/**
 * Validates if a bear-off move is legal.
 * A bear-off is valid when:
 * 1. All player's checkers are in their home board
 * 2. The player is either:
 *    a. Using an exact roll to bear off (die equals the distance from the bearing-off point)
 *    b. Using a larger roll where no checkers exist on higher points
 *
 * @param gameState - The current game state
 * @param player - The player attempting to bear off
 * @param fromIndex - The point index of the checker to bear off
 * @param dieValue - The die value being used for this bear-off move
 * @returns True if the bear-off is valid, false otherwise
 */
function isValidBearOff(gameState, player, fromIndex, dieValue) {
    // 1. First check if the player can bear off at all
    if (!canBearOff(gameState, player)) {
        return false;
    }
    // 2. Check if the player has a checker at the specified point
    if (!gameState.board[fromIndex] ||
        gameState.board[fromIndex].player !== player ||
        gameState.board[fromIndex].count === 0) {
        return false;
    }
    // 3. Calculate the exact distance needed to bear off from this point
    var exactDistance = calculateBearOffDistance(fromIndex, player);
    // 4. Case A: Exact roll matches the distance - always valid
    if (dieValue === exactDistance) {
        return true;
    }
    // 5. Case B: Overshooting - only valid if no checkers exist on higher points
    // For this, we need to find the furthest checker
    if (dieValue > exactDistance) {
        // A) Find the furthest checker in the player's home board
        var furthestCheckerIndex = findFurthestCheckerIndex(gameState.board, player);
        // This should never happen since canBearOff already verified checkers are in home board,
        // but we'll check as a safeguard
        if (furthestCheckerIndex === -1) {
            return true; // No checkers in home board (maybe all on bar or already borne off)
        }
        // B) Check if the fromIndex is the furthest checker's position
        // If player has no checkers on higher points than the one they're moving from,
        // then it's valid to use a larger die value to bear off
        if (player === constants_1.PLAYER_WHITE) {
            return fromIndex >= furthestCheckerIndex; // For white, higher index = further from bearing off
        }
        else {
            return fromIndex <= furthestCheckerIndex; // For black, lower index = further from bearing off
        }
    }
    // Any other scenario is invalid (like trying to bear off with a smaller die than needed)
    return false;
}
/**
 * Checks if a player has won the game by bearing off all checkers.
 *
 * @param player - The player to check
 * @param offCount - Number of checkers that have been borne off
 * @returns True if the player has won, false otherwise
 */
function checkWinCondition(offCount) {
    // In standard backgammon, each player has 15 checkers
    // A player wins when all 15 checkers are borne off
    return offCount === constants_1.CHECKER_COUNT;
}
/**
 * Validates if a sequence of moves is legal according to backgammon rules.
 *
 * @param gameState - The current game state
 * @param moves - The sequence of moves to validate
 * @param dice - The dice values available for this turn
 * @returns True if the move sequence is valid, false otherwise
 */
function isValidMoveSequence(gameState, moves, dice) {
    // We can't make more moves than we have dice
    if (moves.length > dice.length) {
        return false;
    }
    // Clone the game state to avoid modifying the original
    var stateCopy = structuredClone(gameState);
    var diceCopy = tslib_1.__spreadArray([], dice, true);
    // Validate each move in the sequence
    for (var _i = 0, moves_1 = moves; _i < moves_1.length; _i++) {
        var move = moves_1[_i];
        var from = move.from, to = move.to, dieValue = move.dieValue;
        // Check if the die value exists in the available dice
        var dieIndex = diceCopy.indexOf(dieValue);
        if (dieIndex === -1) {
            return false;
        }
        // Check if the individual move is valid on the current state
        if (!isMoveValid(gameState, move)) {
            return false;
        }
        // Apply the move to update the state
        (0, state_1.applyMove)(stateCopy.currentPlayerColor, stateCopy, move);
        // Remove the used die
        diceCopy.splice(dieIndex, 1);
    }
    return true;
}
/**
 * Simulates a sequence of moves and returns the resulting game state.
 * This can be used to preview the result of a sequence of moves without modifying the original state.
 *
 * @param gameState - The starting game state
 * @param moves - The sequence of moves to apply
 * @returns A new game state after applying all moves
 */
function simulateMoveSequence(gameState, moves) {
    // Clone the game state to avoid modifying the original
    var newState = structuredClone(gameState);
    // Apply each move in sequence
    for (var _i = 0, moves_2 = moves; _i < moves_2.length; _i++) {
        var move = moves_2[_i];
        (0, state_1.applyMove)(newState.currentPlayerColor, newState, move);
    }
    return newState;
}
/**
 * Gets all possible starting positions for a player's moves.
 * If there are checkers on the bar, only the bar is a valid starting position.
 * Otherwise, all points with the player's checkers are valid starting positions.
 *
 * @param gameState - The current game state
 * @param player - The player to find starting positions for
 * @returns An array of valid starting indices
 */
function getPossibleFromIndices(gameState, player) {
    // If player has checkers on the bar, they must move those first
    if ((player === constants_1.PLAYER_WHITE && gameState.bar.white > 0) ||
        (player === constants_1.PLAYER_BLACK && gameState.bar.black > 0)) {
        return [player === constants_1.PLAYER_WHITE ? constants_1.BAR_INDEX_WHITE : constants_1.BAR_INDEX_BLACK];
    }
    // Otherwise, find all points with player's checkers
    return gameState.board
        .map(function (point, index) { return (point.player === player && point.count > 0 ? index : -2); })
        .filter(function (index) { return index !== -2; });
}
/**
 * Finds all possible valid moves for a player given the current game state and dice.
 *
 * @param gameState - The current game state
 * @param dice - The dice values available for this turn
 * @returns An array of valid move sequences
 */
function findValidMovesSequence(gameState, dice) {
    // Get the current player
    var player = gameState.currentPlayerColor;
    // If no dice, no moves
    if (dice.length === 0) {
        return [];
    }
    // For doubles, expand the dice array (e.g. [6,6] becomes [6,6,6,6])
    var expandedDice = dice.length === 2 && dice[0] === dice[1] ? [dice[0], dice[0], dice[0], dice[0]] : tslib_1.__spreadArray([], dice, true);
    // Helper function to find moves starting from a specific position
    var findMovesFrom = function (fromIndices, diceToUse, currentSequence, currentState) {
        if (currentSequence === void 0) { currentSequence = []; }
        if (currentState === void 0) { currentState = structuredClone(gameState); }
        // If no more dice, we've completed a move sequence
        if (diceToUse.length === 0) {
            return [currentSequence];
        }
        // No valid starting positions, no more moves
        if (fromIndices.length === 0) {
            return currentSequence.length > 0 ? [currentSequence] : [];
        }
        var allMoves = [];
        // Try each starting position
        for (var _i = 0, fromIndices_1 = fromIndices; _i < fromIndices_1.length; _i++) {
            var fromIndex = fromIndices_1[_i];
            // Try each die
            for (var i = 0; i < diceToUse.length; i++) {
                var die = diceToUse[i];
                // Calculate the target index
                var toIndex = calculateTargetIndex(fromIndex, die, player);
                // Check if move is valid
                var move = {
                    from: fromIndex,
                    to: toIndex,
                    dieValue: die
                };
                if (isMoveValid(gameState, move)) {
                    // Clone and apply move to see resulting state
                    var nextState = structuredClone(currentState);
                    (0, state_1.applyMove)(nextState.currentPlayerColor, nextState, move);
                    // Remove the used die
                    var remainingDice = tslib_1.__spreadArray([], diceToUse, true);
                    remainingDice.splice(i, 1);
                    // Find possible next moves from the new state
                    var nextPossibleFromIndices = getPossibleFromIndices(nextState, player);
                    // Recursively find move sequences
                    var newSequence = tslib_1.__spreadArray(tslib_1.__spreadArray([], currentSequence, true), [move], false);
                    // If no remaining dice, this is a complete move
                    if (remainingDice.length === 0) {
                        allMoves.push(newSequence);
                        continue;
                    }
                    // Try to continue the sequence
                    var continuations = findMovesFrom(nextPossibleFromIndices, remainingDice, newSequence, nextState);
                    // If there are no valid continuations but we've used some dice,
                    // add the current sequence as a valid partial move
                    if (continuations.length === 0 && newSequence.length > 0) {
                        allMoves.push(newSequence);
                    }
                    else {
                        // Add all valid continuations
                        allMoves.push.apply(allMoves, continuations);
                    }
                }
            }
        }
        return allMoves;
    };
    // Get all possible starting positions
    var possibleFromIndices = getPossibleFromIndices(gameState, player);
    // Start the recursive search
    var allMoves = findMovesFrom(possibleFromIndices, expandedDice);
    // Remove duplicates (same sequence of moves)
    var uniqueMoves = removeDuplicateMoves(allMoves);
    return uniqueMoves;
}
/**
 * Finds all valid moves for a player given the current game state and one die value.
 * Returns an array of atomic moves that are valid for this die value.
 *
 * @param gameState - The current game state
 * @param player - The player making the move
 * @param dieValue - The single die value to use
 * @returns An array of valid atomic moves for this die
 */
function findValidMovesForDie(gameState, dieValue) {
    var validMoves = [];
    var player = gameState.currentPlayerColor;
    var playerBarIndex = player === constants_1.PLAYER_WHITE ? constants_1.BAR_INDEX_WHITE : constants_1.BAR_INDEX_BLACK;
    var playerOffIndex = player === constants_1.PLAYER_BLACK ? constants_1.OFF_INDEX_WHITE : constants_1.OFF_INDEX_BLACK;
    // If player has checkers on the bar, they must move those first
    if ((player === constants_1.PLAYER_WHITE && gameState.bar.white > 0) ||
        (player === constants_1.PLAYER_BLACK && gameState.bar.black > 0)) {
        // Calculate target index when entering from bar
        var toIndex = calculateTargetIndex(playerBarIndex, dieValue, player);
        // Check if the move is valid
        if (isMoveValid(gameState, { from: playerBarIndex, to: toIndex, dieValue: dieValue })) {
            validMoves.push({ from: playerBarIndex, to: toIndex, dieValue: dieValue });
        }
        console.log("player has checkers on the bar");
        return validMoves; // If there are checkers on the bar, these are the only possible moves
    }
    // Find points with the player's checkers and check valid moves from each
    gameState.board.forEach(function (point, index) {
        // Skip empty points or points not owned by the player
        if (!point || point.player !== player || point.count === 0) {
            return;
        }
        // Calculate the target index for this move
        var toIndex = calculateTargetIndex(index, dieValue, player);
        // Check if this is a bear-off move
        if (toIndex === playerOffIndex) {
            if (isValidBearOff(gameState, player, index, dieValue)) {
                validMoves.push({ from: index, to: toIndex, dieValue: dieValue });
            }
        }
        // Check if it's a valid normal move
        else if (isMoveValid(gameState, { from: index, to: toIndex, dieValue: dieValue })) {
            validMoves.push({ from: index, to: toIndex, dieValue: dieValue });
        }
    });
    return validMoves;
}
/**
 * Removes duplicate move sequences.
 * Two move sequences are considered duplicates if they result in the same board state.
 *
 * @param moves - Array of move sequences
 * @returns Array of unique move sequences
 */
function removeDuplicateMoves(moves) {
    // If fewer than 2 moves, no duplicates
    if (moves.length <= 1)
        return moves;
    // Use a map to track unique moves by their serialized string
    var uniqueMoves = new Map();
    for (var _i = 0, moves_3 = moves; _i < moves_3.length; _i++) {
        var move = moves_3[_i];
        var moveKey = JSON.stringify(move);
        // Only keep the first occurrence of each unique move
        if (!uniqueMoves.has(moveKey)) {
            uniqueMoves.set(moveKey, move);
        }
    }
    return Array.from(uniqueMoves.values());
}
/**
 * Finds the best move for a player given the current game state and dice.
 * This is a simple heuristic for now and can be expanded later.
 *
 * @param gameState - The current game state
 * @param dice - The dice values available for this turn
 * @returns The best move sequence, or null if no valid moves
 */
function findBestMove(gameState, dice) {
    // Get all valid moves
    var validMoves = findValidMovesSequence(gameState, dice);
    // No valid moves
    if (validMoves.length === 0) {
        return null;
    }
    // For now, use a simple heuristic:
    // 1. Prefer moves that use more dice values
    // 2. For equal dice usage, prefer moves that bear off checkers
    // 3. For equal bearing off, prefer moves that hit opponent checkers
    // Sort moves by these criteria
    validMoves.sort(function (moveA, moveB) {
        // Prefer moves that use more dice
        var diceUsedA = moveA.length;
        var diceUsedB = moveB.length;
        if (diceUsedA !== diceUsedB) {
            return diceUsedB - diceUsedA;
        }
        // Count bearoffs and hits in each move
        var stateA = simulateMoveSequence(gameState, moveA);
        var stateB = simulateMoveSequence(gameState, moveB);
        // Count how many checkers were borne off
        var bearoffDiffA = stateA.off[gameState.currentPlayerColor] - gameState.off[gameState.currentPlayerColor];
        var bearoffDiffB = stateB.off[gameState.currentPlayerColor] - gameState.off[gameState.currentPlayerColor];
        if (bearoffDiffA !== bearoffDiffB) {
            return bearoffDiffB - bearoffDiffA;
        }
        // Count how many opponent checkers were hit
        var opponent = (0, utils_1.getOppositePlayer)(gameState.currentPlayerColor);
        var hitDiffA = stateA.bar[opponent] - gameState.bar[opponent];
        var hitDiffB = stateB.bar[opponent] - gameState.bar[opponent];
        return hitDiffB - hitDiffA;
    });
    // Return the best move
    return validMoves[0];
}
//# sourceMappingURL=index.js.map